import { demoModus } from './api';
import { supabase } from './supabase';

/* ==========================================================================
   Mail versturen vanuit de app.

   De app verstuurt zelf geen mail — dat doet de Edge Function `verstuur-mail`,
   want daar staat de Resend-sleutel en daar mogen de e-mailadressen worden
   opgezocht. Hier gaat alleen een verwijzing de deur uit: welk sjabloon, over
   wie, en eventueel welke poule. De tekst en de ontvangers bepaalt de functie.

   Belangrijk: dit mag nooit een handeling in de weg zitten. Gaat de mail niet
   weg, dan is de goedkeuring of de uitnodiging alsnog gelukt. Daarom gooit
   niets hier een fout; je krijgt een uitkomst terug en mag zelf beslissen of je
   die laat zien.
   ========================================================================== */

/*
 * Eén sjabloon. Een aanmelding en een poule-uitnodiging waren hier ook, maar
 * die zijn meldingen in de app geworden: je ziet ze bij het belletje zodra je
 * binnenkomt, en dat is genoeg. Mail is voor het enige moment waarop je juist
 * níét in de app zit — je bent net vrijgegeven en kon er nog niet in.
 */
export type MailSjabloon = 'account_goedgekeurd';

export interface MailUitkomst {
  ok: boolean;
  /** Aantal ontvangers waar hij werkelijk heen ging. */
  verstuurd: number;
  /** Waarom er niets is verstuurd, als dat een keuze was en geen storing. */
  overgeslagen?: string;
  /** De daglimiet van Resend is bereikt; morgen mag het weer. Zie migratie 021. */
  geblokkeerd?: boolean;
  fout?: string;
}

const NIET_INGESTELD: MailUitkomst = {
  ok: false,
  verstuurd: 0,
  fout: 'de functie verstuur-mail is niet bereikbaar — is hij al uitgerold? (supabase functions deploy verstuur-mail)'
};

/**
 * De melding leesbaar maken. supabase-js kent drie soorten fouten en die zeggen
 * alle drie iets anders:
 *
 *   FunctionsHttpError  — de functie draait en antwoordt met een foutcode; dan
 *                         staat onze eigen uitleg in de body.
 *   FunctionsFetchError — de browser kwam er niet eens langs. In de praktijk
 *                         betekent dat: de functie is nog niet uitgerold. Het
 *                         kale "Failed to send a request to the Edge Function"
 *                         helpt niemand, dus dat vertalen we.
 *   FunctionsRelayError — Supabase kreeg hem niet aan de praat.
 */
async function leesFout(fout: unknown): Promise<{ melding: string; geblokkeerd: boolean }> {
  const zeg = (melding: string, geblokkeerd = false) => ({ melding, geblokkeerd });

  const context = (fout as { context?: Response })?.context;
  if (context && typeof context.json === 'function') {
    try {
      const body = await context.clone().json();
      /* De functie stuurt 429 als de daglimiet op is; dat is geen storing maar
         een grens, en dat mag de app anders verwoorden. */
      if (typeof body?.fout === 'string') return zeg(body.fout, body?.geblokkeerd === true);
    } catch {
      /* geen JSON; dan de melding hieronder */
    }
    if (context.status === 404) return zeg(NIET_INGESTELD.fout as string);
  }

  const naam = (fout as { name?: string })?.name ?? '';
  if (naam === 'FunctionsFetchError' || naam === 'FunctionsRelayError') return zeg(NIET_INGESTELD.fout as string);

  const melding = fout instanceof Error ? fout.message : '';
  if (/failed to send a request/i.test(melding)) return zeg(NIET_INGESTELD.fout as string);

  return zeg(melding || 'De mail ging niet weg.');
}

export async function stuurMail(sjabloon: MailSjabloon, opties: { profielId?: string } = {}): Promise<MailUitkomst> {
  /* In demomodus is er geen functie en geen adres; dan is stilte het juiste. */
  if (demoModus || !supabase) return { ok: true, verstuurd: 0, overgeslagen: 'demomodus' };

  try {
    const { data, error } = await supabase.functions.invoke('verstuur-mail', {
      body: { sjabloon, ...opties }
    });

    if (error) {
      const uit = await leesFout(error);
      return { ok: false, verstuurd: 0, geblokkeerd: uit.geblokkeerd, fout: uit.melding };
    }

    return {
      ok: Boolean(data?.ok),
      verstuurd: Number(data?.verstuurd ?? 0),
      overgeslagen: data?.overgeslagen,
      geblokkeerd: data?.geblokkeerd === true,
      fout: data?.fout ?? undefined
    };
  } catch (e) {
    return { ok: false, verstuurd: 0, fout: e instanceof Error ? e.message : 'De mail ging niet weg.' };
  }
}

/**
 * Voor de plekken waar de mail bijzaak is: doe het, en zeg alleen iets als het
 * misging én er werkelijk iets te melden valt.
 */
export async function stuurMailStil(sjabloon: MailSjabloon, opties: { profielId?: string } = {}): Promise<string | null> {
  const uit = await stuurMail(sjabloon, opties);
  if (uit.ok || uit.overgeslagen) return null;
  return uit.fout ?? 'De mail ging niet weg.';
}

/* --------------------------------------------------------------------------
   Teams-recap

   Dezelfde opzet als de mail: de browser vraagt erom, de Edge Function bepaalt
   wat erin staat en waar het heen gaat. De webhook-URL van Teams is een geheim
   en staat alleen in de omgeving van die functie.
   -------------------------------------------------------------------------- */

export interface RecapUitkomst {
  ok: boolean;
  verstuurd: boolean;
  poules?: number;
  potjes?: number;
  reden?: string;
  fout?: string;
}

/**
 * Eén bevestigd potje in het kanaal. Bijzaak: lukt het niet, dan mag de
 * gebruiker daar niets van merken — hij heeft zijn uitslag gewoon bevestigd.
 */
export async function stuurTeamsPotje(potjeId: string): Promise<void> {
  if (demoModus || !supabase) return;
  try {
    await supabase.functions.invoke('teams-recap', { body: { potjeId } });
  } catch {
    /* stil; het kanaal is geen voorwaarde voor de app */
  }
}

export async function stuurTeamsRecap(opties: { pouleId?: string; dagen?: number } = {}): Promise<RecapUitkomst> {
  if (demoModus || !supabase) return { ok: true, verstuurd: false, reden: 'demomodus' };

  try {
    const { data, error } = await supabase.functions.invoke('teams-recap', { body: opties });
    if (error) {
      const uit = await leesFout(error);
      return { ok: false, verstuurd: false, fout: uit.melding };
    }
    return {
      ok: Boolean(data?.ok),
      verstuurd: data?.verstuurd === true,
      poules: data?.poules,
      potjes: data?.potjes,
      reden: data?.reden,
      fout: data?.fout ?? undefined
    };
  } catch (e) {
    return { ok: false, verstuurd: false, fout: e instanceof Error ? e.message : 'De recap ging niet weg.' };
  }
}
